import React, { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { v4 as uuidv4 } from 'uuid'

import { EuiFlyoutHeader, EuiFlyoutBody, EuiFlyoutFooter, EuiTitle, EuiButton, EuiFlexGroup, EuiFlexItem, EuiText } from '@elastic/eui'
import AcFlyout from 'components/AcFlyout'

import { Persona } from 'api/interfaces/Persona'
import { useGetMasterCampaignPlanQuery, useMasterCampaignPlanEditPersonasMutation } from 'api/rtkQueryApi/platform/masterCampaignPlansApi'
import { RootState } from 'app/rootReducer'
import { PersonaForm, PersonaFormFields } from './PersonaForm'


export enum EditPersonaFlyoutMode {
    Add = 'add',
    Edit = 'edit'
}

export interface EditPersonaFlyoutProps {
    editMode: EditPersonaFlyoutMode
    selectedPersona?: Persona
    initialData?: Persona
    onClose: () => void
}

interface MasterCampaignPlanPageParams {
    masterCampaignPlanId: string
}

const EditPersonaFlyout: React.FC<EditPersonaFlyoutProps> = ({ editMode, selectedPersona, initialData, onClose }) => {
    const { masterCampaignPlanId } = useParams<MasterCampaignPlanPageParams>()
    const { currentAccount, currentOrganization } = useSelector((state: RootState) => state.app)

    const { data: masterCampaignPlan } = useGetMasterCampaignPlanQuery({ masterCampaignPlanId: masterCampaignPlanId! }, { skip: !masterCampaignPlanId })

    const didFireUpdate = useRef(false)
    const [editPersonasMutation, { isLoading: isEditingPersonas, isSuccess: isEditPersonasSuccess }] = useMasterCampaignPlanEditPersonasMutation()

    const [personaFormFields, setPersonaFormFields] = useState<PersonaFormFields>(() => {
        if (editMode === EditPersonaFlyoutMode.Add) {
            if (initialData) {
                return {
                    name: `Copy of ${initialData.name}`,
                    description: initialData.description,
                    demographics: { ...initialData.demographics },
                    triggers: [...initialData.triggers],
                    barriers: [...initialData.barriers],
                    rank: 1,
                    prioritizationReason: initialData.prioritizationReason,
                    segments: [...initialData.segments]
                }
            }
            return {
                name: '',
                description: '',
                demographics: {
                    ageRange: '',
                    gender: '',
                    incomeRange: '',
                    education: '',
                    occupation: '',
                    lifestage: '',
                    homeValue: ''
                },
                triggers: [],
                barriers: [],
                rank: 1,
                prioritizationReason: '',
                segments: []
            }
        }

        if (!selectedPersona) {
            return {
                name: '',
                description: '',
                demographics: {
                    ageRange: '',
                    gender: '',
                    incomeRange: '',
                    education: '',
                    occupation: '',
                    lifestage: '',
                    homeValue: ''
                },
                triggers: [],
                barriers: [],
                rank: 1,
                prioritizationReason: '',
                segments: []
            }
        }

        return {
            name: selectedPersona.name,
            description: selectedPersona.description,
            demographics: { ...selectedPersona.demographics },
            triggers: [...selectedPersona.triggers],
            barriers: [...selectedPersona.barriers],
            rank: selectedPersona.rank,
            prioritizationReason: selectedPersona.prioritizationReason,
            segments: [...selectedPersona.segments]
        }
    })

    useEffect(() => {
        if (didFireUpdate.current && isEditPersonasSuccess) {
            didFireUpdate.current = false
            onClose()
        }
    }, [didFireUpdate, isEditPersonasSuccess, onClose])

    const onSave = () => {
        if (!masterCampaignPlanId || !currentAccount || !currentOrganization || !masterCampaignPlan) {
            return
        }

        const currentPersonas = masterCampaignPlan.personas || []
        let updatedPersonas: Persona[]

        const newPersona: Persona = {
            id: editMode === EditPersonaFlyoutMode.Edit && selectedPersona ? selectedPersona.id : uuidv4(),
            name: personaFormFields.name,
            description: personaFormFields.description,
            demographics: personaFormFields.demographics,
            triggers: personaFormFields.triggers,
            barriers: personaFormFields.barriers,
            rank: personaFormFields.rank,
            prioritizationReason: personaFormFields.prioritizationReason,
            segments: personaFormFields.segments
        }

        if (editMode === EditPersonaFlyoutMode.Add) {
            const shiftedPersonas = currentPersonas.map(persona => ({
                ...persona,
                rank: persona.rank + 1
            }))
            updatedPersonas = [{ ...newPersona, rank: 1 }, ...shiftedPersonas]
        } else {
            if (!selectedPersona) return
            updatedPersonas = currentPersonas.map(persona =>
                persona.name === selectedPersona.name && persona.rank === selectedPersona.rank
                    ? newPersona
                    : persona
            )
        }

        editPersonasMutation({
            organizationId: currentOrganization.id,
            accountId: currentAccount.id,
            masterCampaignPlanId: masterCampaignPlanId,
            personas: updatedPersonas
        })

        didFireUpdate.current = true
    }

    const isSaveDisabled = () => {
        return !personaFormFields.name.trim() ||
            !personaFormFields.description.trim() ||
            !personaFormFields.demographics.ageRange.trim() ||
            !personaFormFields.demographics.gender.trim() ||
            !personaFormFields.demographics.incomeRange.trim() ||
            !personaFormFields.demographics.education.trim() ||
            !personaFormFields.demographics.occupation.trim() ||
            !personaFormFields.demographics.lifestage.trim() ||
            !personaFormFields.demographics.homeValue.trim() ||
            personaFormFields.triggers.length === 0 ||
            personaFormFields.barriers.length === 0
    }

    const getTitle = () => {
        if (editMode === EditPersonaFlyoutMode.Add) {
            return 'Add Persona'
        }
        return `Edit ${personaFormFields.name}`
    }

    return (
        <AcFlyout onClose={onClose} size="m">
            <EuiFlyoutHeader>
                <EuiTitle size="m">
                    <h2>{getTitle()}</h2>
                </EuiTitle>
            </EuiFlyoutHeader>
            <EuiFlyoutBody>
                <PersonaForm
                    personaFormFields={personaFormFields}
                    setPersonaFormFields={setPersonaFormFields}
                />
            </EuiFlyoutBody>
            <EuiFlyoutFooter>
                <EuiFlexGroup direction="row" alignItems="center">
                    <EuiFlexItem grow={false}>
                        <EuiButton onClick={onClose} isDisabled={isEditingPersonas}>
                            Cancel
                        </EuiButton>
                    </EuiFlexItem>
                    <EuiFlexItem grow={false}>
                        <EuiButton
                            fill
                            onClick={onSave}
                            isLoading={isEditingPersonas}
                            isDisabled={isSaveDisabled()}
                        >
                            Save
                        </EuiButton>
                    </EuiFlexItem>
                    <EuiFlexItem grow={false}>
                        <EuiText size="xs">
                            <p>* Required fields</p>
                        </EuiText>
                    </EuiFlexItem>
                </EuiFlexGroup>
            </EuiFlyoutFooter>
        </AcFlyout>
    )
}

export default EditPersonaFlyout 